import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MenuItem from "@mui/material/MenuItem";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import MDInput from "components/MDInput";
import { format, addDays, addWeeks, addMonths } from "date-fns";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress"; // [FIX] Import added
import AddIcon from "@mui/icons-material/Add";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import WorkspaceService from "services/workspace-service";
import BrandingPackDialog from "../../../profile/components/Branding/BrandingPackDialog";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import Checkbox from "@mui/material/Checkbox"; // [FIX] Import added
import ReportGenService from "services/reportgen-service";
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LiveDemoRestriction from "layouts/components/Upgrade/liveDemo"; // [NEW] Custom Popup
import { charts } from "layouts/insight-hub";
import UpgradePopup from "layouts/components/Upgrade";
import { usePermissions } from "context"; // [NEW]

const CustomTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: 400,
        textAlign: "left",
    },
});

const tooltipContent = (
    <div style={{ maxWidth: "350px", textAlign: "left" }}>
        <Typography variant="body2" gutterBottom>
            <strong>Review Your Report:</strong>
        </Typography>
        <Typography variant="caption" display="block" gutterBottom>
            - Confirm report name, recipient emails and delivery preference.<br />
            - Click “Save Report” to finalize your configuration.
        </Typography>
        <Typography variant="body2" gutterBottom>
            <strong>Tip:</strong>
        </Typography>
        <Typography variant="caption" display="block">
            - You can preview the report before saving to see how it looks.
        </Typography>
    </div>
);

function ReportConfiguration({ reportData, defaultReportName, onSave, onCancel, isSaving, selectedProfiles = [], selectedCategories = [], isLiveDemo = false, entitlements = null }) {
    const [brandingPacks, setBrandingPacks] = useState([]);
    const [formData, setFormData] = useState({
        report_name: defaultReportName || "",
        frequency: "monthly",
        recipient_emails: "",
        period: "last_30_days", // Default
        branding_pack_id: "", // Default empty (uses user default)
        target_client: "", // [NEW] Target Client
        scheduleEnabled: false, // Default false (unchecked)
        sendImmediate: false, // Default false for new reports
    });

    const [errors, setErrors] = useState({});
    const [openBrandingDialog, setOpenBrandingDialog] = useState(false);
    const [showLiveDemoPopup, setShowLiveDemoPopup] = useState(false); // [NEW] Popup state
    const [showUpgrade, setShowUpgrade] = useState(false);
    const [upgradeType, setUpgradeType] = useState("feature");
    const [upgradeFeatureName, setUpgradeFeatureName] = useState("Report");
    const [upgradePlanRequired, setUpgradePlanRequired] = useState("Higher Plan");

    // [NEW] RBAC State
    const [showRBACConfig, setShowRBACConfig] = useState(false);
    const [rbacConfigRole, setRbacConfigRole] = useState("Manager");

    const { hasPermission, requiredRoles } = usePermissions();

    // Fetch branding packs
    useEffect(() => {
        const loadBrandingPacks = async () => {
            try {
                const response = await WorkspaceService.fetchWorkspaceBrandingPacks();
                let packs = [];
                // [FIX] UserService returns the array directly (unwrapped by HttpService)
                if (Array.isArray(response)) {
                    packs = response;
                } else if (response && response.data && Array.isArray(response.data)) {
                    packs = response.data;
                }
                setBrandingPacks(packs);

                // [FIX] Auto-select default branding pack for NEW reports (only once on load)
                if (!reportData && packs.length > 0) {
                    const defaultPack = packs.find(p => p.isDefault);
                    if (defaultPack) {
                        setFormData(prev => {
                            // Only set if currently default/empty
                            if (prev.branding_pack_id === "" || prev.branding_pack_id === "default") {
                                return { ...prev, branding_pack_id: defaultPack._id };
                            }
                            return prev;
                        });
                    }
                }
            } catch (error) {
                console.error("Failed to load branding packs", error);
            }
        };
        loadBrandingPacks();
    }, []); // Empty dependency array ensures this runs only once on mount

    useEffect(() => {
        if (reportData) {
            setFormData({
                report_name: reportData.report_name || "",
                frequency: reportData.frequency || "monthly",
                period: reportData.config?.period || "last_30_days", // Load existing or default
                branding_pack_id: reportData.config?.branding_pack_id || reportData.branding_pack_id || "default", // [FIX] Heals legacy data
                target_client: reportData.config?.target_client || reportData.target_client || "", // [FIX] Heals legacy data
                recipient_emails: Array.isArray(reportData.recipient_emails)
                    ? reportData.recipient_emails.join(", ")
                    : reportData.recipient_emails || "",
                sendImmediate: false, // Default false for EXISTING reports
                scheduleEnabled: reportData.scheduleEnabled !== false, // Load existing preference
            });
        } else if (defaultReportName) {
            // Pre-populate only if creating new (no reportData)
            // Ensure branding_pack_id starts as "default" effectively
            setFormData(prev => ({
                ...prev,
                report_name: defaultReportName,
                branding_pack_id: prev.branding_pack_id || "default",
                scheduleEnabled: false, // Explicitly false for new reports
                sendImmediate: false // Explicitly false for new reports
            }));
        }
    }, [reportData, defaultReportName]);

    const validateField = (name, value) => {
        let error = "";
        if (name === "report_name") {
            if (!value.trim()) {
                error = "Report Name is required";
            }
        }
        if (name === "recipient_emails") {
            // [MODIFIED] Only validate if Schedule OR Send Immediate is enabled
            const isDeliveryRequired = formData.scheduleEnabled || formData.sendImmediate;

            if (isDeliveryRequired) {
                if (!value.trim()) {
                    error = "At least one recipient email is required";
                } else {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    const emails = value.split(",").map(e => e.trim());
                    const invalidEmails = emails.filter(email => !emailRegex.test(email));
                    if (invalidEmails.length > 0) {
                        error = `Invalid email(s): ${invalidEmails.join(", ")}`;
                    }
                }
            }
        }
        return error;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Clear error on change if it exists
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: "" }));
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        const error = validateField(name, value);
        if (error) {
            setErrors(prev => ({ ...prev, [name]: error }));
        }
    };

    const validate = (isPreview = false) => {
        const newErrors = {};
        const nameError = validateField("report_name", formData.report_name);
        if (nameError) newErrors.report_name = nameError;

        if (!isPreview) {
            const emailError = validateField("recipient_emails", formData.recipient_emails);
            if (emailError) newErrors.recipient_emails = emailError;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        // [NEW] Live Demo Restriction
        if (isLiveDemo) {
            setShowLiveDemoPopup(true); // [NEW] Show custom popup
            return;
        }

        // 🔒 1. CHECK ENTITLEMENT (Priority)
        const isLocked = entitlements?.features && !entitlements.features.includes("Report");
        if (isLocked) {
            setUpgradeFeatureName("Report");
            setUpgradePlanRequired(entitlements?.requiredPlans?.["Report"] || "Higher Plan");
            setUpgradeType("feature");
            setShowUpgrade(true);
            return;
        }

        // 🔒 2. CHECK USAGE LIMIT (Only for NEW reports)
        if (!reportData) {
            const reportsUsed = entitlements?.usage?.active_reports || 0;
            const reportsLimit = entitlements?.limits?.reports || 0;
            if (Number(reportsUsed) >= Number(reportsLimit)) {
                setUpgradeType("usage");
                setShowUpgrade(true);
                return;
            }
        }

        if (!validate()) return;

        const emails = formData.recipient_emails
            .split(",")
            .map((e) => e.trim())
            .filter((e) => e);

        onSave({
            ...formData,
            branding_pack_id: formData.branding_pack_id === "default" ? "system_default" : formData.branding_pack_id,
            recipient_emails: emails,
            scheduleEnabled: formData.scheduleEnabled,
        });
    };

    const handleBrandingCreated = (newPack) => {
        if (newPack) {
            setBrandingPacks((prev) => [...prev, newPack]);
            setFormData((prev) => ({ ...prev, branding_pack_id: newPack._id }));
        }
        setOpenBrandingDialog(false);
    };

    // Calculate next delivery date
    const getNextDeliveryDate = () => {
        const now = new Date();
        if (formData.frequency === "daily") {
            return format(addDays(now, 1), "MMM dd, yyyy");
        }
        if (formData.frequency === "weekly") {
            return format(addWeeks(now, 1), "MMM dd, yyyy");
        }
        return format(addMonths(now, 1), "MMM dd, yyyy"); // Default to monthly
    };

    const getPeriodDescription = () => {
        const periods = {
            "last_7_days": "Last 7 days",
            "last_14_days": "Last 14 days",
            "last_30_days": "Last 30 days",
            "last_3_months": "Last 3 months"
        };
        return periods[formData.period] || "Last 30 days";
    };

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    // ... existing code ...

    const handlePreview = async () => {
        // 🔒 CHECK ENTITLEMENT
        const DOWNLOAD_FEATURE_KEY = "Report Download";
        const isLocked = entitlements?.features && !entitlements.features.includes(DOWNLOAD_FEATURE_KEY);

        if (isLocked) {
            setUpgradeFeatureName(DOWNLOAD_FEATURE_KEY);
            setUpgradePlanRequired(entitlements?.requiredPlans?.[DOWNLOAD_FEATURE_KEY] || "Higher Plan");
            setUpgradeType("feature");
            setShowUpgrade(true);
            return;
        }

        if (!validate(true)) return;

        try {
            setPreviewLoading(true);

            // 1. Get charts based on categories
            const relevantCharts = charts
                .filter((c) => selectedCategories.includes(c.category))
                .map((c) => c.id);

            // 2. Prepare Profile Data (mimic structure expected by backend)
            const profileData = selectedProfiles.map(p => ({
                profileId: p.id,
                platform: p.platform,
                name: p.name,
                profilePictureUrl: p.profilePictureUrl,
                // Default date range if not set elsewhere? 
                // ReportConfiguration handles period selection, so we calculate dates here or let backend handle relative?
                // Backend expects 'fromDate'/'toDate'.
                // Ideally we use a helper to converting 'last_30_days' to dates.
                ...calculateDateRange(formData.period)
            }));

            // 3. Call Preview Logic
            const url = await ReportGenService.previewReport(
                profileData,
                false, // isPredictionEnabled (default false for now)
                "pdf",
                relevantCharts,
                {}, // chartConfigs
                formData.branding_pack_id === "default" ? "system_default" : formData.branding_pack_id,
                formData.target_client
            );

            setPreviewUrl(url);
            setPreviewOpen(true);

        } catch (err) {
            console.error("Preview failed:", err);
            // setErrors({ submit: "Preview generation failed." }); 
        } finally {
            setPreviewLoading(false);
        }
    };

    // Helper to calculate dates based on period string
    const calculateDateRange = (period) => {
        const toDate = new Date();
        let fromDate = new Date();

        if (period === "last_7_days") fromDate.setDate(toDate.getDate() - 7);
        else if (period === "last_14_days") fromDate.setDate(toDate.getDate() - 14);
        else if (period === "last_3_months") fromDate.setMonth(toDate.getMonth() - 3);
        else fromDate.setDate(toDate.getDate() - 30); // Default last_30_days

        return {
            fromDate: fromDate.toISOString(),
            toDate: toDate.toISOString()
        };
    };

    return (
        <MDBox>
            <Card>
                <MDBox
                    mx={2}
                    mt={-3}
                    py={1}
                    px={2}
                    variant="gradient"
                    bgColor="info"
                    borderRadius="lg"
                    coloredShadow="info"
                    display="flex"
                    alignItems="center"
                >
                    <MDTypography variant="h6" color="white">
                        Review & Save Report
                    </MDTypography>
                    <CustomTooltip title={tooltipContent} placement="bottom" arrow>
                        <IconButton sx={{ color: "white", p: 0, ml: 0.5 }}>
                            <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                    </CustomTooltip>
                </MDBox>
                <MDBox p={3}>
                    {/* ... form content ... */}
                    {/* ... */}

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <MDBox>
                                <MDTypography
                                    variant="button"
                                    fontWeight="medium"
                                    color="text"
                                    sx={{ marginBottom: 1, display: "block", textAlign: "center" }}
                                >
                                    Report Name
                                </MDTypography>
                                <MDBox
                                    sx={{
                                        borderRadius: "16px",
                                        backgroundColor: "#f0f2f5",
                                        padding: "16px",
                                        display: "flex",
                                        alignItems: "center"
                                    }}
                                >
                                    <MDInput
                                        fullWidth
                                        variant="outlined"
                                        name="report_name"
                                        value={formData.report_name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={!!errors.report_name}
                                        helperText={errors.report_name}
                                        sx={{
                                            "& .MuiInputBase-root": {
                                                borderRadius: "12px",
                                                backgroundColor: "white !important",
                                                boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
                                                paddingLeft: "6px",
                                            },
                                        }}
                                    />
                                </MDBox>
                            </MDBox>
                        </Grid>

                        {/* [NEW] Target Client Input */}
                        <Grid item xs={12} md={6}>
                            <MDBox>
                                <MDTypography
                                    variant="button"
                                    fontWeight="medium"
                                    color="text"
                                    sx={{ marginBottom: 1, display: "block", textAlign: "center" }}
                                >
                                    Target Client / Company Name
                                </MDTypography>
                                <MDBox
                                    sx={{
                                        borderRadius: "16px",
                                        backgroundColor: "#f0f2f5",
                                        padding: "16px",
                                        display: "flex",
                                        alignItems: "center"
                                    }}
                                >
                                    <MDInput
                                        fullWidth
                                        variant="outlined"
                                        name="target_client"
                                        placeholder="e.g. Acme Corp (Optional - Leaves blank if empty)"
                                        value={formData.target_client}
                                        onChange={handleChange}
                                        sx={{
                                            "& .MuiInputBase-root": {
                                                borderRadius: "12px",
                                                backgroundColor: "white !important",
                                                boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
                                                paddingLeft: "6px",
                                            },
                                        }}
                                    />
                                </MDBox>
                            </MDBox>
                        </Grid>

                        {/* Merged row: Data Window (sm=4) and Branding Pack (sm=8) */}
                        <Grid item xs={12} sm={4}>
                            <MDBox>
                                <MDTypography
                                    variant="button"
                                    fontWeight="medium"
                                    color="text"
                                    sx={{ marginBottom: 1, display: "block", textAlign: "center" }}
                                >
                                    Data Window
                                </MDTypography>
                                <MDBox
                                    sx={{
                                        borderRadius: "16px",
                                        backgroundColor: "#f0f2f5",
                                        padding: "16px",
                                        height: "100%",
                                        display: "flex",
                                        alignItems: "center"
                                    }}
                                >
                                    <MDInput
                                        select
                                        fullWidth
                                        variant="outlined"
                                        name="period"
                                        value={formData.period}
                                        onChange={handleChange}
                                        SelectProps={{
                                            MenuProps: {
                                                PaperProps: { sx: { maxHeight: 300 } }
                                            },
                                        }}
                                        sx={{
                                            "& .MuiInputBase-root": {
                                                borderRadius: "12px",
                                                backgroundColor: "white !important",
                                                boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
                                                height: "44px"
                                            },
                                            "& .MuiSelect-select": {
                                                paddingTop: "12px",
                                                paddingBottom: "12px",
                                                paddingLeft: "12px"
                                            }
                                        }}
                                    >
                                        <MenuItem value="last_7_days">Last 7 Days</MenuItem>
                                        <MenuItem value="last_14_days">Last 14 Days</MenuItem>
                                        <MenuItem value="last_30_days">Last 30 Days</MenuItem>
                                        <MenuItem value="last_3_months">Last 3 Months</MenuItem>
                                    </MDInput>
                                </MDBox>
                            </MDBox>
                        </Grid>

                        <Grid item xs={12} sm={8}>
                            <MDBox>
                                <MDTypography
                                    variant="button"
                                    fontWeight="medium"
                                    color="text"
                                    sx={{ marginBottom: 1, display: "block", textAlign: "center" }}
                                >
                                    Branding Pack
                                </MDTypography>
                                <MDBox display="flex" alignItems="center" gap={2}
                                    sx={{
                                        borderRadius: "16px",
                                        backgroundColor: "#f0f2f5",
                                        padding: "16px",
                                    }}
                                >
                                    <MDBox sx={{ flex: 1, minWidth: 0 }}>
                                        <MDInput
                                            select
                                            fullWidth
                                            variant="outlined"
                                            name="branding_pack_id"
                                            value={formData.branding_pack_id}
                                            onChange={handleChange}
                                            SelectProps={{
                                                MenuProps: {
                                                    PaperProps: { sx: { maxHeight: 300 } }
                                                },
                                            }}
                                            sx={{
                                                "& .MuiInputBase-root": {
                                                    borderRadius: "12px",
                                                    backgroundColor: "white !important",
                                                    boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
                                                    height: "44px"
                                                },
                                                "& .MuiSelect-select": {
                                                    paddingTop: "12px",
                                                    paddingBottom: "12px",
                                                    paddingLeft: "12px"
                                                }
                                            }}
                                        >
                                            <MenuItem value="default">
                                                <em>Comparedly Default</em>
                                            </MenuItem>
                                            {brandingPacks.map((pack) => (
                                                <MenuItem key={pack._id} value={pack._id}>
                                                    {pack.name}
                                                </MenuItem>
                                            ))}
                                        </MDInput>
                                    </MDBox>
                                    <Card
                                        sx={{
                                            cursor: "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexDirection: "row",
                                            gap: 1,
                                            padding: "0 16px",
                                            borderRadius: "12px",
                                            backgroundColor: "white !important",
                                            boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
                                            height: "44px",
                                            minWidth: "fit-content",
                                            transition: "all 0.3s ease-in-out",
                                            "&:hover": {
                                                transform: "translateY(-1px)",
                                                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
                                            },
                                        }}
                                        onClick={() => {
                                            if (!hasPermission("branding:manage")) {
                                                setRbacConfigRole(requiredRoles["branding:manage"] || "Manager");
                                                setShowRBACConfig(true);
                                                return;
                                            }
                                            setOpenBrandingDialog(true);
                                        }}
                                    >
                                        <IconButton sx={{ p: 0, color: "#e91e63" }}>
                                            <AddCircleIcon fontSize="small" />
                                        </IconButton>
                                        <MDTypography
                                            variant="caption"
                                            fontWeight="bold"
                                            color="text"
                                            sx={{ ml: 0.5, whiteSpace: "nowrap", fontSize: "0.875rem" }}
                                        >
                                            Add New Branding Pack
                                        </MDTypography>
                                    </Card>
                                </MDBox>
                            </MDBox>
                        </Grid>

                        <Grid item xs={12}>
                            <MDBox>
                                <MDTypography
                                    variant="button"
                                    fontWeight="medium"
                                    color="text"
                                    sx={{ marginBottom: 1, display: "block", textAlign: "left" }}
                                >
                                    Recipient Emails (comma separated)
                                </MDTypography>
                                <MDBox
                                    sx={{
                                        borderRadius: "16px",
                                        backgroundColor: "#f0f2f5",
                                        padding: "16px",
                                    }}
                                >
                                    <MDInput
                                        fullWidth
                                        variant="outlined"
                                        name="recipient_emails"
                                        value={formData.recipient_emails}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={!!errors.recipient_emails}
                                        helperText={errors.recipient_emails}
                                        multiline
                                        rows={2}
                                        sx={{
                                            "& .MuiInputBase-root": {
                                                borderRadius: "12px",
                                                backgroundColor: "white !important",
                                                boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
                                                padding: "12px",
                                            },
                                        }}
                                    />
                                </MDBox>
                            </MDBox>

                            {/* Schedule Report Checkbox */}
                            <MDBox display="flex" flexDirection="column" mt={1} ml={0.5} gap={1}>
                                {/* Send Immediate Checkbox (Top) */}
                                <MDBox display="flex" alignItems="center">
                                    <Checkbox
                                        checked={formData.sendImmediate !== false}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setFormData(prev => ({ ...prev, sendImmediate: checked }));
                                            if (!checked && !formData.scheduleEnabled) {
                                                setErrors(prev => ({ ...prev, recipient_emails: "" }));
                                            }
                                        }}
                                        sx={{
                                            p: 0,
                                            mr: 1,
                                            color: "#6c757d",
                                            "& .MuiSvgIcon-root": {
                                                fontSize: 24,
                                                border: formData.sendImmediate !== false ? "none" : "2px solid #adb5bd",
                                                borderRadius: "4px",
                                                fill: formData.sendImmediate !== false ? "currentColor" : "transparent"
                                            },
                                            "&.Mui-checked": { color: "info.main" }
                                        }}
                                    />
                                    <MDTypography variant="button" color="text" fontWeight="medium">
                                        Send a new report immediately now
                                    </MDTypography>
                                </MDBox>

                                {/* Schedule Report Checkbox (Bottom) */}
                                <MDBox display="flex" alignItems="center">
                                    <Checkbox
                                        checked={formData.scheduleEnabled !== false}
                                        onChange={(e) => {
                                            const checked = e.target.checked;

                                            // 🔒 CHECK ENTITLEMENT
                                            if (checked) {
                                                const SCHEDULING_FEATURE_KEY = "Report Scheduling";
                                                const isLocked = entitlements?.features && !entitlements.features.includes(SCHEDULING_FEATURE_KEY);

                                                if (isLocked) {
                                                    setUpgradeFeatureName(SCHEDULING_FEATURE_KEY);
                                                    setUpgradePlanRequired(entitlements?.requiredPlans?.[SCHEDULING_FEATURE_KEY] || "Higher Plan");
                                                    setUpgradeType("feature");
                                                    setShowUpgrade(true);
                                                    return;
                                                }
                                            }

                                            setFormData(prev => ({ ...prev, scheduleEnabled: checked }));
                                            if (!checked && !formData.sendImmediate) {
                                                setErrors(prev => ({ ...prev, recipient_emails: "" }));
                                            }
                                        }}
                                        sx={{
                                            p: 0,
                                            mr: 1,
                                            color: "#6c757d",
                                            "& .MuiSvgIcon-root": {
                                                fontSize: 24,
                                                border: formData.scheduleEnabled !== false ? "none" : "2px solid #adb5bd",
                                                borderRadius: "4px",
                                                fill: formData.scheduleEnabled !== false ? "currentColor" : "transparent"
                                            },
                                            "&.Mui-checked": { color: "info.main" }
                                        }}
                                    />
                                    <MDTypography variant="button" color="text" fontWeight="medium">
                                        Schedule this report to be automatically delivered
                                    </MDTypography>
                                </MDBox>
                            </MDBox>

                            {/* Frequency Selection - Conditionally shown and indented */}
                            {formData.scheduleEnabled !== false && (
                                <MDBox mt={2} ml={0.5}>
                                    <MDBox>
                                        <MDTypography
                                            variant="button"
                                            fontWeight="medium"
                                            color="text"
                                            sx={{ marginBottom: 1, display: "block", textAlign: "center" }}
                                        >
                                            Frequency
                                        </MDTypography>
                                        <MDBox
                                            sx={{
                                                borderRadius: "16px",
                                                backgroundColor: "#f0f2f5",
                                                padding: "16px",
                                                display: "flex",
                                                alignItems: "center",
                                                maxWidth: "300px",
                                                margin: "0 auto"
                                            }}
                                        >
                                            <MDInput
                                                select
                                                fullWidth
                                                variant="outlined"
                                                name="frequency"
                                                value={formData.frequency}
                                                onChange={handleChange}
                                                SelectProps={{
                                                    MenuProps: {
                                                        PaperProps: { sx: { maxHeight: 300 } }
                                                    },
                                                }}
                                                sx={{
                                                    "& .MuiInputBase-root": {
                                                        borderRadius: "12px",
                                                        backgroundColor: "white !important",
                                                        boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
                                                        height: "44px"
                                                    },
                                                    "& .MuiSelect-select": {
                                                        paddingTop: "12px",
                                                        paddingBottom: "12px",
                                                        paddingLeft: "12px"
                                                    },
                                                }}
                                            >
                                                <MenuItem value="daily">Daily</MenuItem>
                                                <MenuItem value="weekly">Weekly</MenuItem>
                                                <MenuItem value="monthly">Monthly</MenuItem>
                                            </MDInput>
                                        </MDBox>
                                    </MDBox>
                                </MDBox>
                            )}
                        </Grid>

                        {/* Details & Action Row */}
                        <Grid item xs={12}>
                            <MDBox
                                bgcolor="grey-100"
                                borderRadius="lg"
                                p={2}
                                display="flex"
                                alignItems={{ xs: "flex-start", md: "center" }}
                                justifyContent="space-between"
                                flexDirection={{ xs: "column", md: "row" }}
                                gap={3}
                            >
                                {/* Left: Details */}
                                <MDBox display="flex" alignItems="flex-start" gap={2}>
                                    <Icon color="info" fontSize="small" sx={{ mt: 0.5 }}>info</Icon>
                                    <MDBox sx={{ textAlign: "left" }}>
                                        <MDTypography variant="button" fontWeight="bold" color="text" display="block">
                                            Report Details
                                        </MDTypography>
                                        {formData.scheduleEnabled !== false ? (
                                            <MDTypography variant="caption" color="text" display="block">
                                                <strong>Trigger:</strong> Next scheduled delivery on <strong>{getNextDeliveryDate()}</strong> ({formData.frequency}).
                                            </MDTypography>
                                        ) : (
                                            <MDTypography variant="caption" color="text" display="block">
                                                This report will be saved but not scheduled for automatic delivery.
                                            </MDTypography>
                                        )}
                                        <MDTypography variant="caption" color="text" display="block">
                                            <strong>Content:</strong> Each report will cover data for the <strong>{getPeriodDescription()}</strong> prior to the trigger date.
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>

                                {/* Right: Actions */}
                                <MDBox display="flex" justifyContent="flex-end" gap={2} width={{ xs: "100%", md: "auto" }}>
                                    {!isLiveDemo && (
                                        <MDButton onClick={handlePreview} variant="outlined" color="dark" disabled={isSaving || previewLoading}>
                                            {previewLoading ? <CircularProgress size={20} color="accent" /> : "Preview Report"}
                                        </MDButton>
                                    )}
                                    <MDButton onClick={onCancel} color="secondary" disabled={isSaving || previewLoading}>
                                        Cancel
                                    </MDButton>
                                    <MDButton onClick={handleSubmit} variant="contained" color="accent" disabled={isSaving || previewLoading}>
                                        {isSaving ? (
                                            <MDBox display="flex" alignItems="center" gap={1}>
                                                <CircularProgress size={20} color="white" />
                                                Saving...
                                            </MDBox>
                                        ) : (
                                            "Save Report"
                                        )}
                                    </MDButton>
                                </MDBox>
                            </MDBox>
                        </Grid>
                    </Grid>
                </MDBox>
            </Card>

            <BrandingPackDialog
                open={openBrandingDialog}
                onClose={() => setOpenBrandingDialog(false)}
                onSuccess={handleBrandingCreated}
                isLiveDemo={isLiveDemo} // [NEW] Pass restriction flag
            />

            {/* Preview Dialog */}
            <Dialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: { height: "90vh", maxWidth: "1200px" }
                }}
            >
                <DialogTitle display="flex" justifyContent="space-between" alignItems="center">
                    Report Preview
                    <IconButton onClick={() => setPreviewOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0, overflow: "hidden" }}>
                    {previewUrl && (
                        <iframe
                            src={previewUrl}
                            width="100%"
                            height="100%"
                            title="Report Preview"
                            style={{ border: "none" }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* [NEW] Live Demo Restriction Popup */}
            <LiveDemoRestriction
                open={showLiveDemoPopup}
                onClose={() => setShowLiveDemoPopup(false)}
                featureName="Saving Report"
            />

            <UpgradePopup
                open={showUpgrade}
                onClose={() => setShowUpgrade(false)}
                type={upgradeType}
                featureName={upgradeFeatureName}
                planRequired={upgradePlanRequired}
                currentPlan={entitlements?.plan}
                usageLimit={entitlements?.limits?.reports}
                unit="reports"
            />

            {/* [NEW] RBAC Restriction Popup */}
            <UpgradePopup
                open={showRBACConfig}
                onClose={() => setShowRBACConfig(false)}
                type="rbac"
                featureName="Managing Branding Packs"
                roleRequired={rbacConfigRole}
            />
        </MDBox >
    );
}

export default ReportConfiguration;
